export interface ManualSequence {
  sequenceIndex: number;
  instruction: string;
  example: string;
}

export interface Manual {
  manualId: string;
  title: string;
  overview: string;
  comment?: string | null;
  sequences: ManualSequence[];
  updateAt: string;
}

export interface CreateManualRequest {
  title: string;
  overview: string;
  comment?: string | null;
  sequences: ManualSequence[];
}

export interface UpdateManualRequest {
  title: string;
  overview: string;
  comment?: string | null;
  sequences: ManualSequence[];
}

export class ManualClient {
  private readonly headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Origin: window.location.origin,
  };

  constructor(private readonly apiUrl: string) {}

  public async listManuals(): Promise<Manual[]> {
    try {
      const response = await fetch(`${this.apiUrl}/manual`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch manuals: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching manuals:', error);
      throw error;
    }
  }

  public async getManual(manualId: string): Promise<Manual> {
    try {
      const response = await fetch(`${this.apiUrl}/manual/${manualId}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch manual: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error(`Error fetching manual with ID ${manualId}:`, error);
      throw error;
    }
  }

  public async createManual(request: CreateManualRequest): Promise<Manual> {
    try {
      const response = await fetch(`${this.apiUrl}/manual`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to create manual: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error creating manual:', error);
      throw error;
    }
  }

  public async updateManual(manualId: string, request: UpdateManualRequest): Promise<Manual> {
    try {
      const response = await fetch(`${this.apiUrl}/manual/${manualId}`, {
        method: 'PUT',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to update manual with ID ${manualId}: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error(`Error updating manual with ID ${manualId}:`, error);
      throw error;
    }
  }

  public async deleteManual(manualId: string): Promise<void> {
    try {
      const response = await fetch(`${this.apiUrl}/manual/${manualId}`, {
        method: 'DELETE',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete manual with ID ${manualId}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Error deleting manual with ID ${manualId}:`, error);
      throw error;
    }
  }
}
